import React, { useState, useRef, useEffect, useCallback } from 'react';

// ── Palette aligned with the Production Dashboard / SpkCreateDialog
const C = {
  indigo: '#4f46e5',
  ink: '#111827',
  slate: '#475569',
  sub: '#64748b',
  muted: '#94a3b8',
  line: '#e2e8f0',
  panel: '#f8fafc',
  white: '#fff',
  // Critical (red)
  red: '#dc2626',
  redBg: '#fef2f2',
  redBorder: '#fecaca',
  // Planned (amber)
  amber: '#d97706',
  amberBg: '#fffbeb',
  amberBorder: '#fde68a',
  // Neutral
  neutral: '#64748b',
  neutralBg: '#f1f5f9',
  neutralBorder: '#e2e8f0',
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ── A single alert row, preserving the exact red/amber distinction of the
//    previous inline chips.
const AlertRow = ({ a }) => {
  const isCritical = a.status.step === 0;
  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 8,
        fontSize: 13, fontWeight: 600,
        background: isCritical ? C.redBg : C.amberBg,
        color: isCritical ? C.red : C.amber,
        border: `1px solid ${isCritical ? C.redBorder : C.amberBorder}`,
      }}
    >
      <i className={isCritical ? 'pi pi-exclamation-circle' : 'pi pi-info-circle'} style={{ fontSize: 16, marginTop: 1, flexShrink: 0 }}></i>
      <span style={{ minWidth: 0 }}>
        <b>{a.kode}</b> {a.nama}: {isCritical ? 'DEFISIT tapi belum dijadwalkan!' : 'Sudah direncana, belum masuk produksi'}
      </span>
    </div>
  );
};

const AlertsPopup = ({ alerts = [] }) => {
  const [open, setOpen] = useState(false);

  const triggerRef = useRef(null);
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);

  const count = alerts.length;
  const criticalCount = alerts.reduce((n, a) => n + (a.status.step === 0 ? 1 : 0), 0);
  const displayCount = count > 99 ? '99+' : String(count);

  // ── Trigger tone: red when any critical, amber when alerts exist but none
  //    critical, neutral when there are no alerts.
  let tone;
  if (criticalCount > 0) {
    tone = { bg: C.redBg, color: C.red, border: C.redBorder, icon: 'pi-exclamation-circle' };
  } else if (count > 0) {
    tone = { bg: C.amberBg, color: C.amber, border: C.amberBorder, icon: 'pi-info-circle' };
  } else {
    tone = { bg: C.neutralBg, color: C.neutral, border: C.neutralBorder, icon: 'pi-bell' };
  }

  // ── Close: restore focus to the trigger, or to the first dashboard control.
  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      const el = triggerRef.current;
      if (el && document.body.contains(el) && typeof el.focus === 'function') {
        el.focus();
        return;
      }
      const focusable = document.querySelector(FOCUSABLE);
      if (focusable && typeof focusable.focus === 'function') focusable.focus();
    }, 0);
  }, []);

  // ── Move focus into the popup on open (first focusable = close button).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      (closeBtnRef.current || panelRef.current)?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  // ── Keyboard: Escape closes, Tab is trapped within the popup.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }
    if (e.key === 'Tab') {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onOverlayMouseDown = (e) => {
    if (e.target === overlayRef.current) handleClose();
  };

  return (
    <>
      {/* ═══ Compact single-row trigger (replaces the inline chip block) ═══ */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={count > 0 ? `${count} alert produksi` : 'Tidak ada alert produksi'}
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden',
          padding: '6px 12px', borderRadius: 8,
          background: tone.bg, color: tone.color,
          border: `1px solid ${tone.border}`,
          fontWeight: 700, fontSize: 13, cursor: 'pointer', lineHeight: 1.2,
        }}
      >
        <i className={`pi ${tone.icon}`} style={{ fontSize: 15, flexShrink: 0 }}></i>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Alert Produksi</span>
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 22, height: 20, padding: '0 7px', borderRadius: 999,
            background: tone.color, color: C.white, fontSize: 12, fontWeight: 800, flexShrink: 0,
          }}
        >
          {displayCount}
        </span>
      </button>

      {/* ═══ Popup ═══ */}
      {open && (
        <div
          ref={overlayRef}
          onMouseDown={onOverlayMouseDown}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: 16,
          }}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Daftar alert produksi"
            tabIndex={-1}
            onKeyDown={onKeyDown}
            style={{
              background: C.white, borderRadius: 12, width: 'min(560px, 94vw)', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 20px 50px rgba(15,23,42,.30)',
              borderTop: `3px solid ${criticalCount > 0 ? C.red : count > 0 ? C.amber : C.indigo}`,
              outline: 'none',
            }}
          >
            {/* ── Header (fixed) ── */}
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.line}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className={`pi ${tone.icon}`} style={{ color: tone.color, fontSize: 15 }} />
                  <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>Alert Produksi</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: C.white, background: tone.color, padding: '1px 8px', borderRadius: 999 }}>{displayCount}</span>
                </div>
                <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
                  Item defisit yang belum masuk produksi
                </div>
              </div>
              <button
                ref={closeBtnRef}
                type="button"
                aria-label="Tutup"
                onClick={handleClose}
                style={{ border: 'none', background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4, borderRadius: 6 }}
              >
                <i className="pi pi-times" />
              </button>
            </div>

            {/* ── Body (scrolls vertically) ── */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {count === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '28px 16px', color: C.muted, textAlign: 'center' }}>
                  <i className="pi pi-check-circle" style={{ fontSize: 28, color: '#16a34a' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.slate }}>Tidak ada alert produksi saat ini.</span>
                </div>
              ) : (
                alerts.map((a) => <AlertRow key={a.kode} a={a} />)
              )}
            </div>

            {/* ── Action area (fixed) ── */}
            <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'flex-end', background: C.white }}>
              <button
                type="button"
                onClick={handleClose}
                style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${C.line}`, background: C.white, color: C.slate, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlertsPopup;
